import {
  athenaQuery,
  AFFILIATION_ID,
  COUNTRY,
  isTheaterOpen,
} from './AthenaApi';

/*
 * Site-wide cinema list for the header dropdown / city datalist (CinemaContext).
 *
 * It's the same ~790-cinema list on every page and rarely changes, so it's cached
 * with a stale-while-revalidate strategy:
 *   1. in-memory (module scope)  — free across client-side navigations in one load
 *   2. localStorage (24h TTL)    — survives reloads / new tabs / sessions
 * The cached list is served immediately; a stale (or missing) cache triggers a
 * background refetch that updates both layers. Bump CACHE_VERSION if the stored
 * shape changes so old blobs are ignored.
 */

export type SelectCinema = {
  node: {
    name: string;
    location: { city: string };
  };
};

const STORAGE_KEY = 'ukca.selectCinemas';
const CACHE_VERSION = 3; // bump to invalidate old caches (sorted; closed theaters excluded)
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const PAGE_SIZE =
  typeof process.env.GATSBY_API_LIMIT !== 'undefined'
    ? parseInt(process.env.GATSBY_API_LIMIT, 10)
    : 120;

const isBrowser = typeof window !== 'undefined';

let memoryCache: SelectCinema[] | null = null;
let inFlight: Promise<SelectCinema[]> | null = null;

type CachedResult = { data: SelectCinema[]; stale: boolean };

// Synchronous read: in-memory first (always fresh within a page load), then
// localStorage (flagged stale past the TTL). Returns null when there's nothing
// usable to render (incl. during SSR, where window/localStorage don't exist).
export const readCachedSelectCinemas = (): CachedResult | null => {
  if (memoryCache) {
    return { data: memoryCache, stale: false };
  }
  if (!isBrowser) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      parsed.version !== CACHE_VERSION ||
      !Array.isArray(parsed.data)
    ) {
      return null;
    }
    memoryCache = parsed.data;
    return { data: parsed.data, stale: Date.now() - parsed.fetchedAt > TTL_MS };
  } catch (e) {
    return null;
  }
};

const writeCache = (data: SelectCinema[]) => {
  memoryCache = data;
  if (!isBrowser) {
    return;
  }
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: CACHE_VERSION, fetchedAt: Date.now(), data })
    );
  } catch (e) {
    // Storage disabled or over quota — the in-memory cache still applies.
  }
};

// Paginate the full list, then persist it. Concurrent callers share one request.
export const fetchSelectCinemas = (): Promise<SelectCinema[]> => {
  if (inFlight) {
    return inFlight;
  }

  const all: SelectCinema[] = [];

  const makeCall = (offset: number): Promise<SelectCinema[]> => {
    const query = `
      query GetSelectCinemas {
        theaters(
          affiliationId: "${AFFILIATION_ID}",
          iso_3166_1_a2: "${COUNTRY}",
          limit: ${PAGE_SIZE},
          offset: ${offset}
        ) {
          totalCount
          nodes {
            name
            address { city }
            opening { status }
          }
        }
      }`;

    return athenaQuery(query, 'theaters').then((r: any) => {
      if (r === null) {
        return all;
      }
      const { totalCount, nodes } = r.data.theaters;
      nodes.filter(isTheaterOpen).forEach((node: any) => {
        all.push({
          node: {
            name: node.name,
            location: { city: node.address ? node.address.city : '' },
          },
        });
      });
      if (offset + PAGE_SIZE < totalCount) {
        return makeCall(offset + PAGE_SIZE);
      }
      // Athena has no server-side ordering arg (the old API used order:
      // [ALPHABETICAL]), so sort by name client-side for the dropdown.
      all.sort((a, b) => a.node.name.localeCompare(b.node.name, 'en'));
      return all;
    });
  };

  inFlight = makeCall(0)
    .then((data) => {
      writeCache(data);
      inFlight = null;
      return data;
    })
    .catch((e) => {
      inFlight = null;
      throw e;
    });

  return inFlight;
};
